import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Trash2, ShieldCheck } from "lucide-react";
import HCaptcha from "@hcaptcha/react-hcaptcha";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  },
};

const dataItems = [
  "Información de tu cuenta (nombre, correo electrónico)",
  "Historial de rituales y preferencias de bienestar",
  "Datos de uso y configuración de la aplicación",
  "Cualquier contenido generado dentro de la app",
];

export default function DataDeletionForm() {
  const [status, setStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    setErrorMessage("");

    const form = e.target;
    const formData = new FormData(form);
    const data = Object.fromEntries(formData);

    if (!captchaToken) {
      setStatus("error");
      setErrorMessage("Por favor verifica el captcha e intenta de nuevo.");
      setLoading(false);
      return;
    }

    data["h-captcha-response"] = captchaToken;
    delete data["g-recaptcha-response"];

    const json = JSON.stringify(data);

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: json,
      });
      const result = await res.json();

      if (result.success) {
        setStatus("success");
        form.reset();
        setCaptchaToken("");
        if (captchaRef.current) captchaRef.current.resetCaptcha();
      } else {
        setStatus("error");
        setErrorMessage(result.message || "Error desconocido del servidor.");
      }
    } catch (err) {
      setStatus("error");
      setErrorMessage("Error de conexión. Intenta más tarde.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#050505] text-white relative noise-overlay"
      data-testid="deletion-page"
    >
      {/* Decorative gold glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-[#D4AF37]/3 blur-[150px] pointer-events-none" />

      <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 py-24 md:py-32">
        {/* Back link */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-white/40 hover:text-[#D4AF37] transition-colors duration-300 mb-12 group"
            aria-label="Volver al inicio"
          >
            <ArrowLeft
              size={16}
              className="group-hover:-translate-x-1 transition-transform duration-300"
            />
            Volver al inicio
          </Link>
        </motion.div>

        {/* Title */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            visible: { transition: { staggerChildren: 0.1 } },
          }}
        >
          <motion.h1
            variants={fadeUp}
            className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1] mb-4 text-white"
          >
            Eliminar{" "}
            <span className="italic text-[#D4AF37]">Cuenta</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="text-sm text-white/30 mb-16 tracking-wide"
          >
            Solicitud de eliminación de cuenta y datos personales
          </motion.p>
        </motion.div>

        {/* Info section */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="mb-12"
        >
          <p className="text-base md:text-lg text-white/60 leading-relaxed mb-8">
            En Serena respetamos tu derecho a la privacidad. Puedes solicitar la
            eliminación completa de tu cuenta y todos los datos asociados
            utilizando el formulario a continuación.
          </p>

          {/* What data will be deleted */}
          <div className="bg-white/[0.02] border border-white/5 rounded-lg p-6 md:p-8 mb-8">
            <div className="flex items-center gap-3 mb-5">
              <Trash2 size={20} className="text-[#D4AF37]/70" />
              <h2 className="font-serif text-lg md:text-xl text-white/90">
                Datos que se eliminarán
              </h2>
            </div>
            <ul className="space-y-3">
              {dataItems.map((item, index) => (
                <li
                  key={index}
                  className="text-sm md:text-base text-white/50 leading-relaxed pl-4 border-l-2 border-[#D4AF37]/10"
                >
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Response timeframe */}
          <div className="bg-white/[0.02] border border-white/5 rounded-lg p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4">
              <ShieldCheck size={20} className="text-[#D4AF37]/70" />
              <h2 className="font-serif text-lg md:text-xl text-white/90">
                Proceso de eliminación
              </h2>
            </div>
            <p className="text-sm md:text-base text-white/50 leading-relaxed">
              Una vez recibida tu solicitud, procesaremos la eliminación de tu
              cuenta y datos en un plazo máximo de{" "}
              <span className="text-[#D4AF37]/80 font-medium">
                10 días hábiles
              </span>
              . Recibirás una confirmación por correo electrónico cuando el
              proceso haya finalizado. Esta acción es irreversible.
            </p>
          </div>
        </motion.div>

        {/* Divider */}
        <div className="my-12 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent" />

        {/* Form */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <h2 className="font-serif text-2xl md:text-3xl font-medium tracking-tight mb-8 text-white/90">
            Solicitar{" "}
            <span className="italic text-[#D4AF37]">eliminación</span>
          </h2>

          <form
            onSubmit={handleSubmit}
            className="space-y-6 max-w-lg"
            data-testid="deletion-form"
          >
            <input
              type="hidden"
              name="access_key"
              value="ae7f687c-709c-49f9-9f94-5425e531d2fd"
            />
            <input
              type="hidden"
              name="subject"
              value="Solicitud de Borrado de Cuenta y Datos - Serena"
            />
            <input
              type="hidden"
              name="from_name"
              value="Serena - Borrado de Datos"
            />

            {/* Email */}
            <div>
              <label
                htmlFor="deletion-email"
                className="block text-xs uppercase tracking-[0.15em] text-white/40 mb-2"
              >
                Correo electrónico *
              </label>
              <input
                id="deletion-email"
                type="email"
                name="email"
                placeholder="tu@email.com"
                required
                className="w-full bg-white/5 border border-white/10 focus:border-[#D4AF37] px-5 py-3.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/50 transition-all duration-300 rounded"
                data-testid="deletion-email-input"
              />
            </div>

            {/* Name */}
            <div>
              <label
                htmlFor="deletion-name"
                className="block text-xs uppercase tracking-[0.15em] text-white/40 mb-2"
              >
                Nombre (opcional)
              </label>
              <input
                id="deletion-name"
                type="text"
                name="name"
                placeholder="Tu nombre"
                className="w-full bg-white/5 border border-white/10 focus:border-[#D4AF37] px-5 py-3.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/50 transition-all duration-300 rounded"
                data-testid="deletion-name-input"
              />
            </div>

            {/* Reason */}
            <div>
              <label
                htmlFor="deletion-reason"
                className="block text-xs uppercase tracking-[0.15em] text-white/40 mb-2"
              >
                Motivo (opcional)
              </label>
              <textarea
                id="deletion-reason"
                name="reason"
                rows={4}
                placeholder="Cuéntanos por qué deseas eliminar tu cuenta (opcional)"
                className="w-full bg-white/5 border border-white/10 focus:border-[#D4AF37] px-5 py-3.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/50 transition-all duration-300 rounded resize-none"
                data-testid="deletion-reason-input"
              />
            </div>

            {/* hCaptcha */}
            <HCaptcha
              sitekey="50b2fe65-b00b-4b9e-ad62-3ba471098be2"
              ref={captchaRef}
              theme="dark"
              onVerify={(token) => setCaptchaToken(token)}
              onExpire={() => setCaptchaToken("")}
            />

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto bg-[#D4AF37] text-black px-10 py-3.5 text-xs uppercase tracking-wider font-semibold hover:bg-[#C5A059] transition-all duration-300 disabled:opacity-50 rounded"
              data-testid="deletion-submit-btn"
            >
              {loading ? "Enviando..." : "Solicitar eliminación"}
            </button>
          </form>

          {/* Status messages */}
          {status === "success" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-lg p-5"
              data-testid="deletion-success-msg"
            >
              <p className="text-[#D4AF37] text-sm">
                Tu solicitud de eliminación ha sido recibida. Procesaremos tu
                solicitud en un plazo máximo de 10 días hábiles y te enviaremos
                una confirmación por correo electrónico.
              </p>
            </motion.div>
          )}
          {status === "error" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 bg-red-500/10 border border-red-500/20 rounded-lg p-5"
              data-testid="deletion-error-msg"
            >
              <p className="text-red-400 text-sm">
                {errorMessage || "Ocurrió un error. Intenta de nuevo."}
              </p>
            </motion.div>
          )}
        </motion.div>

        {/* Divider */}
        <div className="my-20 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent" />

        {/* Footer note */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="text-center"
        >
          <p className="text-xs text-white/20 mb-6">
            © 2025 Serena. Todos los derechos reservados. Hecho en México.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[#D4AF37]/70 hover:text-[#D4AF37] transition-colors duration-300"
            aria-label="Volver al inicio"
          >
            <ArrowLeft size={14} />
            Volver al inicio
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
